import { type CSSProperties } from 'react';

const fontFamily = 'Roboto';

export const monospaceFontFamily = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const editorFontFamily = {
    primary: 'Sole Serif',
    secondary: 'Sole Serif',
    subtitle: 'Libre Franklin',
};

export type TextStyles = {
    fontFamily: CSSProperties['fontFamily'];
    fontSize: CSSProperties['fontSize'];
    fontWeight: CSSProperties['fontWeight'];
    lineHeight?: CSSProperties['lineHeight'];
};

type Size = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';
type Variant = 'body' | 'subtitle' | 'title';
type EditorVariant = 'primary' | 'secondary' | 'subtitle';

export const baseConfig: Record<Variant, Partial<Record<Size, TextStyles>>> & {
    editor: Record<EditorVariant, Partial<Record<Size, TextStyles>>>;
} = {
    body: {
        XS: {
            fontFamily,
            fontSize: '10px',
            fontWeight: 'normal',
            lineHeight: '16px',
        },
        S: {
            fontFamily,
            fontSize: '12px',
            fontWeight: 'normal',
            lineHeight: '16px',
        },
        M: {
            fontFamily,
            fontSize: '14px',
            fontWeight: 'normal',
            lineHeight: '20px',
        },
        L: {
            fontFamily,
            fontSize: '16px',
            fontWeight: 'normal',
            lineHeight: '24px',
        },
        XL: {
            fontFamily,
            fontSize: '22px',
            fontWeight: 'normal',
            lineHeight: '32px',
        },
    },
    subtitle: {
        XS: {
            fontFamily,
            fontSize: '10px',
            fontWeight: 500,
            lineHeight: '16px',
        },
        S: {
            fontFamily,
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '16px',
        },
        M: {
            fontFamily,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
        },
        L: {
            fontFamily,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
        },
        XL: {
            fontFamily,
            fontSize: '22px',
            fontWeight: 500,
            lineHeight: '32px',
        },
    },
    title: {
        XS: {
            fontFamily,
            fontSize: '11px',
            fontWeight: 'bold',
        },
        S: {
            fontFamily,
            fontSize: '14px',
            fontWeight: 'bold',
            lineHeight: '18px',
        },
        M: {
            fontFamily,
            fontSize: '16px',
            fontWeight: 'bold',
            lineHeight: '24px',
        },
        L: {
            fontFamily,
            fontSize: '22px',
            fontWeight: 'bold',
            lineHeight: '32px',
        },
        XL: {
            fontFamily,
            fontSize: '48px',
            fontWeight: 'bold',
            lineHeight: '48px',
        },
    },
    editor: {
        primary: {
            S: {
                fontFamily: editorFontFamily.primary,
                fontSize: '14px',
                fontWeight: 'normal',
                lineHeight: '19px',
            },
            M: {
                fontFamily: editorFontFamily.primary,
                fontSize: '18px',
                fontWeight: 'normal',
                lineHeight: '28px',
            },
            L: {
                fontFamily: editorFontFamily.primary,
                fontSize: '20px',
                fontWeight: 'normal',
                lineHeight: '30px',
            },
            XL: {
                fontFamily: editorFontFamily.primary,
                fontSize: '22px',
                fontWeight: 'normal',
                lineHeight: '30px',
            },
        },
        secondary: {
            XS: {
                fontFamily: editorFontFamily.secondary,
                fontSize: '12px',
                fontWeight: 'bold',
                lineHeight: '18px',
            },
            S: {
                fontFamily: editorFontFamily.secondary,
                fontSize: '14px',
                fontWeight: 'bold',
                lineHeight: '18px',
            },
            M: {
                fontFamily: editorFontFamily.secondary,
                fontSize: '20px',
                fontWeight: 'bold',
                lineHeight: '28px',
            },
            L: {
                fontFamily: editorFontFamily.secondary,
                fontSize: '24px',
                fontWeight: 'bold',
                lineHeight: '30px',
            },
            XL: {
                fontFamily: editorFontFamily.secondary,
                fontSize: '36px',
                fontWeight: 'bold',
                lineHeight: '45px',
            },
            XXL: {
                fontFamily: editorFontFamily.secondary,
                fontSize: '49px',
                fontWeight: 'bold',
                lineHeight: '52px',
            },
        },
        subtitle: {
            XS: {
                fontFamily: editorFontFamily.subtitle,
                fontSize: '12px',
                fontWeight: 'normal',
                lineHeight: '20px',
            },
            S: {
                fontFamily: editorFontFamily.subtitle,
                fontSize: '14px',
                fontWeight: 'normal',
                lineHeight: '20px',
            },
            M: {
                fontFamily: editorFontFamily.subtitle,
                fontSize: '16px',
                fontWeight: 'normal',
                lineHeight: '28px',
            },
            L: {
                fontFamily: editorFontFamily.subtitle,
                fontSize: '18px',
                fontWeight: 'normal',
                lineHeight: '30px',
            },
        },
    },
};
